"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Input, Badge } from "reactstrap"
import DataTable from "react-data-table-component"
import { Btn } from "../../../AbstractElements"
import {
  ParticipationService,
  type Participation as ServiceParticipation,
} from "../../../Service/ExhibitionRelatedService"
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa"
import { toast } from "react-toastify"

interface Props {
  exhibitionId: number
  onAdd: () => void
  onEdit: (item: ServiceParticipation) => void
  refresh: boolean
}

const ParticipationsTab: React.FC<Props> = ({ exhibitionId, onAdd, onEdit, refresh }) => {
  const [data, setData] = useState<ServiceParticipation[]>([])
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await ParticipationService.getAll(exhibitionId)
      console.log("Participations data:", response) // Debug log
      setData(response.results || [])
    } catch (err) {
      console.error("Failed to fetch participations:", err)
      toast.error("Failed to fetch participations")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [exhibitionId, refresh])

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this participation?")) {
      try {
        await ParticipationService.delete(id)
        toast.success("Participation deleted successfully")
        fetchData()
      } catch {
        toast.error("Failed to delete participation")
      }
    }
  }

  const getExhibitorName = (item: ServiceParticipation): string => {
    if (typeof item.exhibitor === "object" && item.exhibitor?.name) {
      return item.exhibitor.name
    }
    if (typeof item.exhibitor === "string") {
      return item.exhibitor
    }
    return item.exhibitor_name || "N/A"
  }

  const getExhibitionTitle = (item: ServiceParticipation): string => {
    if (typeof item.exhibition === "object" && item.exhibition?.title) {
      return item.exhibition.title
    }
    if (typeof item.exhibition === "string") {
      return item.exhibition
    }
    return item.exhibition_title || "N/A"
  }

  const getStandCategoryName = (item: ServiceParticipation): string => {
    if (typeof item.stand_category === "object" && item.stand_category?.name) {
      return item.stand_category.name
    }
    if (typeof item.stand_category === "string") {
      return item.stand_category
    }
    return item.stand_category_name || "N/A"
  }

  const filteredData = data.filter(
    (item) =>
      getExhibitorName(item).toLowerCase().includes(search.toLowerCase()) ||
      getExhibitionTitle(item).toLowerCase().includes(search.toLowerCase()) ||
      getStandCategoryName(item).toLowerCase().includes(search.toLowerCase()) ||
      String(item.status || "")
        .toLowerCase()
        .includes(search.toLowerCase()),
  )

  const getStatusColor = (status: any) => {
    const statusStr = String(status).toLowerCase()
    switch (statusStr) {
      case "confirmed":
      case "active":
      case "true":
        return "success"
      case "pending":
        return "warning"
      case "cancelled":
      case "inactive":
      case "false":
        return "danger"
      default:
        return "secondary"
    }
  }

  const getStatusDisplay = (status: any): string => {
    if (typeof status === "boolean") {
      return status ? "Active" : "Inactive"
    }
    if (typeof status === "string") {
      return status.charAt(0).toUpperCase() + status.slice(1)
    }
    return String(status || "Unknown")
  }

  const columns = [
    // {
    //   name: "Exhibition",
    //   selector: (row: ServiceParticipation) => getExhibitionTitle(row),
    //   sortable: true,
    // },
    {
      name: "Exhibitor",
      selector: (row: ServiceParticipation) => getExhibitorName(row),
      sortable: true,
    },
    {
      name: "Stand Category",
      selector: (row: ServiceParticipation) => getStandCategoryName(row),
      sortable: true,
    },
    {
      name: "Status",
      cell: (row: ServiceParticipation) => (
        <Badge color={getStatusColor(row.status)} pill>
          {getStatusDisplay(row.status)}
        </Badge>
      ),
    },
    // {
    //   name: "Created By",
    //   selector: (row: ServiceParticipation) => row.created_by || "N/A",
    //   sortable: true,
    // },
    {
      name: "Created At",
      selector: (row: ServiceParticipation) => (row.created_at ? new Date(row.created_at).toLocaleDateString() : "N/A"),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row: ServiceParticipation) => (
        <div className="d-flex gap-1">
          {/* <Btn color="info" size="xs" onClick={() => onEdit(row)} title="Edit">
            <FaEdit size={12} />
          </Btn> */}
          <Btn color="danger" size="xs" onClick={() => handleDelete(row.id)} title="Delete">
            <FaTrash size={12} />
          </Btn>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ]

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Input
          type="search"
          placeholder="Search participations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 300 }}
        />
        {/* <Btn color="primary" size="sm" onClick={onAdd}>
          <FaPlus className="me-1" size={12} /> Add Participation
        </Btn> */}
      </div>

      <DataTable
        columns={columns}
        data={filteredData}
        progressPending={loading}
        pagination
        striped
        highlightOnHover
        responsive
        noDataComponent="No participations found."
      />
    </div>
  )
}

export default ParticipationsTab
